import { memo, useState, useEffect, useCallback } from "react";
import Form from "../../components/Form.js";
import Popup from "../../components/Popup.js";
import useSelectStyles from "../../components/styles/Select.js";
import { useSnackbar } from "../../utils/index.js";
import { Typography, Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import Spinner from "../../components/Spinner.js";
import { getOwners, calculateTotalMilk } from "../../api/index.js";
import { pdf } from "@react-pdf/renderer";
import MilkCalculationIssuesPdf from "./MilkCalculationIssuesPdf.js";
import MilkYieldResults, { recordingMethodLabel, standardMethodLabel } from "./MilkYieldResults.js";
import MilkCalculationProblem from "./MilkCalculationProblem.js";
import { getMilkCalculationProblems, getMilkCalculationIssueRows, milkProblemExplanation } from "../../utils/milk-calculation-messages.js";

const makeAnimalIssueRows = (first = [], second = [], third = [], firstMax = null, maxInterval = null) =>
    getMilkCalculationIssueRows([], [first, second, third].flatMap((animals, index) => animals.map((animal) => ({
        animalId: animal._id || animal.registrationCode || animal.flockCode,
        flockCode: animal.flockCode,
        registrationCode: animal.registrationCode,
        code: `LEGACY_STATUS_${index + 1}`,
        calculation: { firstTestMax: firstMax, maxIntervalBetweenChecks: maxInterval },
    }))));

const csvCell = (value) => {
    const text = String(value ?? "");
    const safeText = /^[=+\-@\t\r]/.test(text) ? `'${text}` : text;
    return `"${safeText.replaceAll('"', '""')}"`;
};

const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const formatDate = (date) => (date ? new Date(date).toLocaleDateString("el-GR") : "");

const totalMilkCalculation = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [fetchedOwners, setFetchedOwners] = useState([]);
    const [ownersDropdownValues, setOwnersDropdownValues] = useState([]);
    const [datesDropdownValues, setDatesDropdownValues] = useState([]);
    const [isInformPopupOpen, setIsInformPopupOpen] = useState(false);
    const [animalIssueRows, setAnimalIssueRows] = useState([]);
    const [incompleteMilkRecordings, setIncompleteMilkRecordings] = useState([]);
    const [calculatedPeriods, setCalculatedPeriods] = useState([]);
    const [selectedOwner, setSelectedOwner] = useState(null);
    const [sDate, setSDate] = useState(null);
    const [eDate, setEDate] = useState(null);

    const currentYear = new Date().getFullYear();
    const today = new Date();
    const augustFirst = new Date(currentYear, 7, 1);
    const startYear = today >= augustFirst ? currentYear : currentYear - 1;
    const years = Array.from({ length: startYear - 1999 + 1 }, (_, i) => startYear - i);
    const dates = years.map((year) => ({
        value: `Αύγουστος ${year} - Ιούλιος ${year + 1}`,
        text: `Αύγουστος ${year} - Ιούλιος ${year + 1}`,
        startDateValue: new Date(year, 7, 1),
        endDateValue: new Date(year + 1, 6, 31, 23, 59, 59, 999),
    }));

    const classes = useSelectStyles();
    const { error } = useSnackbar();
    const ownerName = selectedOwner
        ? `${selectedOwner.lastName || ""} ${selectedOwner.firstName || ""} - ${selectedOwner.code || ""}`.trim()
        : "";
    const periodLabel = `${formatDate(sDate)} - ${formatDate(eDate)}`;
    const hasExportRows = animalIssueRows.length > 0 || incompleteMilkRecordings.length > 0;

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const { owners } = await getOwners();
            setFetchedOwners(owners);
            setOwnersDropdownValues(owners.map((owner) => ({
                value: `${owner?.lastName} ${owner?.firstName} - ${owner?.code}`,
                text: `${owner?.lastName} ${owner?.firstName} - ${owner?.code}`,
            })));
        } catch (err) {
            error(err);
        }
        setIsLoading(false);
    }, [error]);

    useEffect(() => {
        fetchData();
        setDatesDropdownValues(dates);
    }, []);

    const selectOwnerDateFormContent = [
        { customType: "dropdown", id: "owner", items: ownersDropdownValues, placeholder: "Εκτροφή", required: true },
        { customType: "dropdown", id: "date", items: datesDropdownValues, placeholder: "Περίοδος Τοκετών", required: true },
        { customType: "button", id: "submit", type: "submit", text: "Υποβολή" },
    ];

    const handleSubmit = async (values) => {
        setIsLoading(true);
        try {
            const owner = fetchedOwners.find((item) => `${item?.lastName} ${item?.firstName} - ${item?.code}` === values.owner);
            const selectedDate = dates.find((date) => date.text === values.date);
            const startDate = selectedDate?.startDateValue;
            const endDate = selectedDate?.endDateValue;
            setSelectedOwner(owner || null);
            setSDate(startDate);
            setEDate(endDate);

            const response = await calculateTotalMilk({
                ownerId: owner?._id,
                startDate,
                endDate,
            });
            const {
                success: successResponse,
                totalMilkProduced = {},
                firstTypeErrorAnimals = [],
                secondTypeErrorAnimals = [],
                thirdTypeErrorAnimals = [],
                firstMax,
                maxIntervalBetweenChecks,
                incompleteMilkRecordings: incompleteRecordings = [],
                calculatedPeriods: periods = [],
                calculationIssues,
            } = response;

            if (!successResponse) {
                error("Ο υπολογισμός δεν ολοκληρώθηκε. Δοκιμάστε ξανά ή επικοινωνήστε με τον υπεύθυνο της πλατφόρμας.");
                return;
            }

            if (Object.keys(totalMilkProduced).length === 0) {
                error("Δεν βρέθηκαν στοιχεία για την εκτροφή και την περίοδο που επιλέξατε.");
                return;
            }

            const issuesByPeriod = new Map();
            (calculationIssues || []).forEach((issue) => {
                const key = `${issue.animalId}-${issue.lambingId}`;
                if (!issuesByPeriod.has(key)) issuesByPeriod.set(key, []);
                issuesByPeriod.get(key).push(issue);
            });
            const resolvedPeriods = periods.map((period) => ({
                ...period,
                calculation: { ...period.calculation,
                    issues: [...(period.calculation?.issues || []), ...(issuesByPeriod.get(`${period.animalId}-${period.lambingId}`) || [])],
                },
            }));
            const issueRows = Array.isArray(calculationIssues)
                ? getMilkCalculationIssueRows(resolvedPeriods, calculationIssues)
                : [...getMilkCalculationIssueRows(resolvedPeriods), ...makeAnimalIssueRows(
                    firstTypeErrorAnimals, secondTypeErrorAnimals, thirdTypeErrorAnimals, firstMax, maxIntervalBetweenChecks)];
            const incompleteWarnings = incompleteRecordings.map((recording) => ({
                ...recording,
                warning: `Στον έλεγχο της ${formatDate(recording.testDate)}, ${recording.incompleteAnimals} ζώα έχουν ελλιπή στοιχεία. Δείτε παρακάτω τι χρειάζεται να διορθωθεί.`,
            }));
            setCalculatedPeriods(resolvedPeriods);
            setAnimalIssueRows(issueRows);
            setIncompleteMilkRecordings(incompleteWarnings);
            setIsInformPopupOpen(true);
        } catch (err) {
            error("Ο υπολογισμός δεν ολοκληρώθηκε. Δοκιμάστε ξανά ή επικοινωνήστε με τον υπεύθυνο της πλατφόρμας.");
        } finally {
            setIsLoading(false);
        }
    };

    const exportResultsCsv = () => {
        const columns = ["Εκτροφή", "Περίοδος", "Κωδικός ζώου", "Κωδικός μητρώου", "Τοκετός", "Πραγματική (kg)", "Τυπική (kg)", "Ημέρα στόχος", "Μέθοδος τυπικής", "Μέθοδος υπολογισμού", "Πρόβλημα", "Επεξήγηση"];
        const rows = calculatedPeriods.map((period) => [
            ownerName, periodLabel, period.flockCode, period.registrationCode, formatDate(period.lambingDate),
            period.milk, period.standardMilk, period.calculation?.standardDays, standardMethodLabel(period.calculation),
            recordingMethodLabel(period.calculation),
            getMilkCalculationProblems(period).map((problem) => problem.title).join(" | ") || "Χωρίς προβλήματα",
            getMilkCalculationProblems(period).map(milkProblemExplanation).join(" | "),
        ]);
        const content = [columns, ...rows].map((row) => row.map(csvCell).join(";")).join("\r\n");
        downloadBlob(new Blob(["\uFEFF", content], { type: "text/csv;charset=utf-8;" }), "ypologismos_galaktoparagogis_apotelesmata.csv");
    };

    const exportCsv = () => {
        const columns = ["Κατηγορία", "Κωδικός εκτροφής", "Περίοδος", "Κωδικός ζώου", "Κωδικός μητρώου", "Πρόβλημα", "Επεξήγηση", "Ημερομηνία ελέγχου", "Ζώα με ελλιπή μέτρηση"];
        const rows = [
            ...animalIssueRows.map((row) => ["Πρόβλημα ζώου", ownerName, periodLabel, row.flockCode, row.registrationCode, row.title, milkProblemExplanation(row), formatDate(row.testDate), ""]),
            ...incompleteMilkRecordings.map((recording) => ["Ελλιπής καταχώρηση", ownerName, periodLabel, "", "", "Ελλιπή στοιχεία ελέγχου", recording.warning, formatDate(recording.testDate), recording.incompleteAnimals]),
        ];
        const content = [columns, ...rows].map((row) => row.map(csvCell).join(";")).join("\r\n");
        downloadBlob(new Blob(["\uFEFF", content], { type: "text/csv;charset=utf-8;" }), "ypologismos_galaktoparagogis_provlimata.csv");
    };

    const exportPdf = async () => {
        setIsLoading(true);
        try {
            const blob = await pdf(
                <MilkCalculationIssuesPdf
                    ownerName={ownerName}
                    periodLabel={periodLabel}
                    animalIssueRows={animalIssueRows}
                    incompleteMilkRecordings={incompleteMilkRecordings}
                />,
            ).toBlob();
            downloadBlob(blob, "ypologismos_galaktoparagogis_provlimata.pdf");
        } catch (exportError) {
            console.error(exportError);
            error("Δεν δημιουργήθηκε το PDF. Δοκιμάστε ξανά τη λήψη του.");
        }
        setIsLoading(false);
    };

    return (
        <>
            <Spinner open={isLoading} />
            <Popup
                width="min(96vw, 1100px)"
                open={isInformPopupOpen}
                title={`Ολοκληρώθηκαν οι υπολογισμοί γαλακτοπαραγωγής για την περίοδο ${periodLabel}`}
                titleColor="black"
                onClose={() => setIsInformPopupOpen(false)}
            >
                <Typography sx={{ color: "dark.main", mb: 1 }}>
                    Εκτροφή: {ownerName}. Υπολογίστηκε πραγματική παραγωγή για {calculatedPeriods.filter((period) => period.milk != null).length} ζώα
                    και τυπική παραγωγή για {calculatedPeriods.filter((period) => period.standardMilk != null).length} ζώα.
                </Typography>
                <Typography sx={{ mb: 1 }}>
                    {animalIssueRows.length === 0 && incompleteMilkRecordings.length === 0
                        ? "Ο υπολογισμός ολοκληρώθηκε χωρίς προβλήματα."
                        : "Ο υπολογισμός ολοκληρώθηκε. Υπάρχουν προβλήματα σε ορισμένα ζώα. Δείτε τη λίστα παρακάτω."}
                </Typography>
                <Box sx={{ maxHeight: "55vh", overflowY: "auto", pr: 1, mb: 2 }}>
                    {calculatedPeriods.length > 0 && <MilkYieldResults periods={calculatedPeriods} />}
                    {incompleteMilkRecordings.length > 0 && (
                        <Box sx={{ mb: 2, p: 1.5, color: "error.main", backgroundColor: "#fff4e5" }}>
                            <Typography fontWeight={700}>Προειδοποιήσεις ελλιπών μετρήσεων</Typography>
                            {incompleteMilkRecordings.map((recording) => (
                                <Typography key={String(recording.milkRecordingId)}>{recording.warning}</Typography>
                            ))}
                            <Typography variant="body2">Για τις παρακάτω περιόδους δεν υπολογίστηκε το γάλα. Συμπληρώστε τα στοιχεία που λείπουν και κάντε ξανά τον υπολογισμό.</Typography>
                        </Box>
                    )}
                    {animalIssueRows.length > 0 ? (
                        <TableContainer sx={{ border: "1px solid #ddd" }}>
                            <Table stickyHeader size="small" aria-label="Προβλήματα υπολογισμού ανά ζώο">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Κωδικός ζώου</TableCell>
                                        <TableCell>Κωδικός μητρώου</TableCell>
                                        <TableCell>Πρόβλημα</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {animalIssueRows.map((row) => (
                                        <TableRow key={row.id}>
                                            <TableCell>{row.flockCode || "—"}</TableCell>
                                            <TableCell>{row.registrationCode || "—"}</TableCell>
                                            <TableCell><MilkCalculationProblem problem={row} /></TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        incompleteMilkRecordings.length === 0 && <Typography>Δεν βρέθηκαν προβλήματα στα ζώα.</Typography>
                    )}
                </Box>
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, flexWrap: "wrap" }}>
                    <Button variant="contained" onClick={exportResultsCsv} disabled={calculatedPeriods.length === 0}>Αποτελέσματα CSV</Button>
                    <Button variant="outlined" onClick={exportCsv} disabled={!hasExportRows}>Προβλήματα CSV</Button>
                    <Button variant="outlined" onClick={exportPdf} disabled={!hasExportRows}>Προβλήματα PDF</Button>
                    <Button variant="contained" color="secondary" onClick={() => setIsInformPopupOpen(false)}>Κλείσιμο</Button>
                </Box>
            </Popup>
            <Box className={classes.outerBox}>
                <Box flexDirection="column" className={classes.innerBox}>
                    <Typography variant="h5" sx={{ margin: "1rem" }}>
                        Επιλέξτε την Εκτροφή και την ημερομηνία που σας ενδιαφέρει:
                    </Typography>
                    <Form content={selectOwnerDateFormContent} onSubmit={handleSubmit} toResetForm />
                </Box>
            </Box>
        </>
    );
};

export default memo(totalMilkCalculation);
